/**
 * digilockerParamCache
 *
 * Persists onboarding URL params to localStorage before the user is redirected
 * to the Digio / DigiLocker external page, and restores them when they return.
 *
 * Why localStorage (not sessionStorage):
 *  - Some device/browser combos open the Digio redirect in a new tab; when the
 *    user is sent back the original tab still needs the cached params.
 *  - The cache is keyed per-session via a timestamp and cleared immediately
 *    after a successful restore, so stale data cannot accumulate.
 */

import { SEARCH_PARAMS } from 'core/constants';

const CACHE_KEY = 'onb_digilocker_params';

export interface DigilockerCachedParams {
  [key: string]: string | undefined;
}


/**
 * Call this right before triggering the Digio redirect.
 * Only non-empty values are persisted.
 */
export const saveDigilockerParams = (params: DigilockerCachedParams): void => {
  try {
    const toSave: Partial<DigilockerCachedParams> = {};

    (Object.keys(params) as (keyof DigilockerCachedParams)[]).forEach((key) => {
      const value = params[key];
      if (value && value.trim().length > 0) {
        toSave[key] = value;
      }
    });

    if (Object.keys(toSave).length > 0) {
      localStorage.setItem(CACHE_KEY, JSON.stringify(toSave));
    }
  } catch {
    // localStorage unavailable (private mode, storage quota, etc.) — fail silently
  }
};

/**
 * Call this when returning from Digio (i.e. `digio_doc_id` is present in URL).
 * Returns the cached params, or null if nothing was saved.
 */
export const getDigilockerParams = (): DigilockerCachedParams | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    // Basic shape validation — must be a plain object with known keys only
    if (typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    const knownKeys = new Set(Object.values(SEARCH_PARAMS));
    const safe: DigilockerCachedParams = {};

    Object.entries(parsed).forEach(([k, v]) => {
      if (knownKeys.has(k) && typeof v === 'string' && v.trim().length > 0) {
        (safe as Record<string, string>)[k] = v;
      }
    });

    return Object.keys(safe).length > 0 ? safe : null;
  } catch {
    return null;
  }
};

/**
 * Call this after params have been restored so stale data cannot be re-applied.
 */
export const clearDigilockerParams = (): void => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // fail silently
  }
};
